/**
 * 通用瀑布流组件
 * 支持自定义渲染项、响应式布局、高性能
 * 适用于移动端和PC端
 */

'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import type { WaterfallProps } from './types';

/**
 * 通用瀑布流容器组件
 */
export function Waterfall<T = any>({
  data,
  renderItem,
  columns = 2,
  gap = 8,
  itemWidth,
  className = '',
  style = {},
  responsive = true,
  baseWidth = 375,
  defaultItemWidth = 170,
  minHeight = 300,
  onDataChange,
}: WaterfallProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [itemHeights, setItemHeights] = useState<number[]>([]);
  const itemHeightsRef = useRef<number[]>([]); // 存储最新的itemHeights值
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  // 同步itemHeights到ref
  useEffect(() => {
    itemHeightsRef.current = itemHeights;
  }, [itemHeights]);

  // 响应式尺寸计算
  const [calculatedItemWidth, setCalculatedItemWidth] = useState(itemWidth || defaultItemWidth);
  const [calculatedGap, setCalculatedGap] = useState(gap);

  // 响应式尺寸计算
  useEffect(() => {
    if (!responsive || itemWidth) return;

    const calculateDimensions = () => {
      if (typeof window === 'undefined') {
        return {
          itemWidth: defaultItemWidth,
          gap,
        };
      }

      const screenWidth = window.innerWidth;
      const scale = screenWidth / baseWidth;

      return {
        itemWidth: Math.floor(defaultItemWidth * scale),
        gap: Math.floor(gap * scale),
      };
    };

    // 初始计算
    const initialDimensions = calculateDimensions();
    setCalculatedItemWidth(initialDimensions.itemWidth);
    setCalculatedGap(initialDimensions.gap);

    // 监听窗口变化
    const handleResize = () => {
      const resizeDimensions = calculateDimensions();
      setCalculatedItemWidth(resizeDimensions.itemWidth);
      setCalculatedGap(resizeDimensions.gap);
    };

    window.addEventListener('resize', handleResize);

    // 延迟再次计算，确保窗口尺寸已稳定
    const timer = setTimeout(() => {
      const delayedDimensions = calculateDimensions();
      setCalculatedItemWidth(delayedDimensions.itemWidth);
      setCalculatedGap(delayedDimensions.gap);
    }, 100);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [responsive, itemWidth, baseWidth, defaultItemWidth, gap]);

  // 监听数据变化
  useEffect(() => {
    if (onDataChange) {
      onDataChange(data);
    }
  }, [data, onDataChange]);

  // 使用ResizeObserver监听所有子元素的高度变化
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;
    const items = Array.from(container.children) as HTMLElement[];

    if (items.length === 0) return;

    // 清理旧的observer
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
    }

    // 立即获取一次高度（避免等待ResizeObserver回调）
    const initialHeights: number[] = [];
    items.forEach((item, index) => {
      const height = item.offsetHeight || 0;
      initialHeights[index] = height;
    });

    // 如果有高度数据，立即设置（减少闪烁）
    if (initialHeights.some((h) => h > 0)) {
      setItemHeights(initialHeights);
      itemHeightsRef.current = initialHeights; // 同步更新ref
    }

    // 创建ResizeObserver用于后续动态调整
    resizeObserverRef.current = new ResizeObserver(() => {
      const heights: number[] = [];
      items.forEach((item, index) => {
        heights[index] = item.offsetHeight || 0;
      });

      // 只有高度真的改变了才更新（避免不必要的重渲染）
      const currentHeights = itemHeightsRef.current;
      const hasChanged = heights.some((h, i) => h !== currentHeights[i]);
      if (hasChanged) {
        setItemHeights(heights);
      }
    });

    // 监听所有子元素
    items.forEach((item) => {
      resizeObserverRef.current?.observe(item);
    });

    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, [data.length]); // 移除itemHeights依赖，避免无限循环

  // 基于实际高度计算每个item的位置
  const { itemPositions, containerHeight } = useMemo(() => {
    if (itemHeights.length === 0) {
      return { itemPositions: [], containerHeight: minHeight };
    }

    const positions: Array<{ x: number; y: number }> = [];
    const columnHeights = new Array(columns).fill(0);

    itemHeights.forEach((height, index) => {
      // 找到最短的列
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));

      const x = shortestColumnIndex * (calculatedItemWidth + calculatedGap);
      const y = columnHeights[shortestColumnIndex];

      positions[index] = { x, y };
      columnHeights[shortestColumnIndex] = y + height + calculatedGap;
    });

    const maxHeight = Math.max(...columnHeights);

    return {
      itemPositions: positions,
      containerHeight: maxHeight || minHeight,
    };
  }, [itemHeights, columns, calculatedItemWidth, calculatedGap, minHeight]);

  return (
    <div
      ref={containerRef}
      className={`relative ${className}`}
      style={{
        height: `${containerHeight}px`,
        ...style,
      }}
    >
      {data.map((item, index) => {
        // 尝试使用item的唯一标识作为key，避免重新挂载
        const itemKey = (item as any)?.id || (item as any)?.sku?.shop_sku || index;

        return (
          <div
            key={itemKey}
            className="absolute"
            style={{
              width: `${calculatedItemWidth}px`,
              left: `${itemPositions[index]?.x || 0}px`,
              top: `${itemPositions[index]?.y || 0}px`,
            }}
          >
            {renderItem(item, index)}
          </div>
        );
      })}
    </div>
  );
}

export default Waterfall;
export type { WaterfallDimensions, WaterfallItemPosition, WaterfallProps } from './types';
